import { AllGeoJSON, bbox, bboxPolygon, centroid } from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  MMS_PIN_SOURCE_ID,
  PIN_TYPE_FEATURE,
  PIN_TYPE_MMS,
} from '../constants';

export enum BaseGeometryType {
  POINT = 'POINT',
  POLYGON = 'POLYGON',
  LINESTRING = 'LINESTRING',
  POINT_AREA_HIGHLIGHT = 'POINT_AREA_HIGHLIGHT',
  POLYGON_AREA_HIGHLIGHT = 'POLYGON_AREA_HIGHLIGHT',
  DISPLAY_POINT = 'DISPLAY_POINT',
}

export default class LMFeature {
  private readonly mapboxFeature: MapboxGeoJSONFeature;

  constructor(mapboxFeature: MapboxGeoJSONFeature) {
    this.mapboxFeature = mapboxFeature;
  }

  public getId(): number {
    if (this.getProperty('pin_id')) {
      return this.getProperty('pin_id') as number;
    }
    return this.getProperty('uid') as number;
  }

  /**
   * We use 3 different levels of categorisation, where the ordering is:
   *  - Category (broad)
   *  - Class (finer)
   *  - Type (detailed)
   *
   *
   * This function returns the highest detailed categorisation.
   *
   * do note that although a type is a string, it could have a different context depending on what category and class it belongs to.
   * @returns string
   */
  public getType(): string {
    return this.getProperty('type') as string;
  }

  /**
   * when a geometry does not have attributes, we would generally refrain from having it be clickable, and searchable.
   * Though this defenition has always been slightly vague and is in need for more verbose specification.
   * @returns boolean
   */
  public getHasAttributes(): boolean {
    return this.getProperty('is_geojson') as boolean;
  }

  /**
   * Returns a URL formatted string that represents a URL might be emtpy as well.
   *
   * @returns string
   */
  public getImageURL(): string {
    return this.getProperty('popup_image_url') as string;
  }

  /**
   * Not 100% sure what this means, and how this is used. Database team to provide.
   * @returns {string}
   */
  public getName(): string {
    return (this.getProperty('popup_header') ||
      this.getProperty('name')) as string;
  }

  public getSub(): string {
    return this.getProperty('popup_subheader') as string;
  }

  /**
   * Returns a string that is formatted like Markdown.
   * Note: please ensure its santized before using this anywhere.
   * @returns {string}
   */
  public getDescription(): string {
    return this.getProperty('popup_body') as string;
  }

  /**
   * Gives back a loosely formatted string that is the address of the feature.
   * @returns {string}
   */
  public getStreetAddress(): string {
    return this.getProperty('street_address') as string;
  }

  /**
   * Returns a telephone number related to the feature.
   * @returns {string}
   */
  public getTelephoneNumber(): string | null {
    return this.getProperty('tel_number') as string | null;
  }

  /**
   * Returns a loosely formatted comman seperated string containing the opening hours per day.
   * Do note that this format might differ and is not set in stone.
   * @returns {string}
   */
  public getOpeningHours(): string | null {
    return this.getProperty('opening_times') as string | null;
  }

  /**
   * Returns a URL relevant to the feature dontents.
   * Note: the database over time has not always been very consistent in its URL formatting, and might or might not have a `http` / `https` protocol.
   * @returns {string | null}
   */
  public getUrl(): string | null {
    return this.getProperty('url') as string | null;
  }

  /**
   * Returns a URL relevant to the feature dontents with a protocol attached.
   * @returns {string | null}
   */
  public getUrlWithHttp(): string | null {
    const url = this.getProperty('url') as string | null;
    if (url === null) return null;
    return url.startsWith('http') ? url : 'http://' + url;
  }

  public getUrlStripped(): string | null {
    const url = this.getProperty('url');
    if (url === null) {
      return null;
    }

    const match = url.toString().match(/^.+:\/\/(.+)/);
    return (match ? match[1] : url) as string;
  }

  /**
   * in the case the geometry is a transport geometry, this function will return the id this is related to. These ids
   * could then be used by our transport API service to retrieve appropriate data from them.
   *
   * a few examples of a return value would be:
   * - `departures/global:gtfs:ets:train:1754, global:gtfs:ets:train:1925`
   * - `departures/uk:transport:train:tiploc:BATHSPA`
   * - `departures/uk:transport:bus:0180BAC31066`
   *
   * @returns string
   */
  public getTransportStops(): string | null {
    return this.getProperty('transport_api') as string | null;
  }

  // TODO: requires unit testing
  /**
   * @returns {number} internal ID for tracking floor data.
   */
  public getFloorId(): number | null {
    const floorId = this.getProperty('floor_id') as number;
    return floorId;
  }

  /**
   * returns whether the geometry is "indoor" or "outdoor".
   * @returns boolean
   */
  public isIndoor(): boolean {
    return this.getProperty('indoor') as boolean;
  }

  /**
   * given the geometry of the feature this will calculate the centroid.
   * Note that sometimes the feature geometry is just a point, in which case the output will be the same.
   * @returns {number[]} coordinate
   */
  public getCentroid(): number[] | undefined {
    const point = centroid(this.getGeometry() as AllGeoJSON);
    return point.geometry?.coordinates;
  }

  /**
   * returns a fitting bounding box around the feature geometry.
   * @returns {Feature} a geojson polygon feature
   */
  public getBoundingBox(): Feature<Polygon, {}> {
    const boundingBox = bbox(this.getGeometry() as AllGeoJSON);
    return bboxPolygon(boundingBox);
  }

  /**
   * This function will return whether or not this geometry is a transport geometry.
   * A transport geometry could be a bus stop, train station, ferry pickup point etc. etc.
   * When it is a transport geometry, it also will have a unique ID to link it to our transport service.
   *
   * @returns {boolean} Whether this geometry is a transport geometry
   */
  public isTransportFeature(): boolean {
    const transportStops = this.getTransportStops();
    return Boolean(transportStops);
  }

  public isLiveDataFeature(): boolean {
    const liveData = this.getProperty('isLiveData');
    return Boolean(liveData);
  }

  /**
   * Returns what type of geometry data we are dealing with. Currently will cover the basic 3 core geometry types
   * and a few additional area highlight geometries.
   * @returns {BaseGeometryType}
   */
  public getGeomTypeProperty(): BaseGeometryType {
    return this.getProperty('geom_type') as BaseGeometryType;
  }

  /**
   * Returns a location name. Although the definition of a location name is not set in stone,
   * in general this would refer to a sub "area". This could be terminals, given an airport map,
   * it could be neighbourhoods when revering to a city etc. etc.
   * @returns string
   */
  public getLocationName(): string {
    return this.getProperty('location_name') as string;
  }

  /**
   * Gives a unique ID associated to the location the feature is in.
   * @returns number
   */
  public getLocationId(): number {
    return this.getProperty('location_id') as number;
  }

  /**
   * the mapbox source layer which this geometry is located in.
   * this value is only available if the geometry is from a MVT source.
   * @returns {string | undefined}
   */
  public getSourceLayer(): string | undefined {
    return this.getMapboxFeature().sourceLayer;
  }

  /**
   * the mapbox source which this geometry is located in. Usually you can expect this to be "lm" for Living Map data.
   * @returns {string}
   */
  public getSource(): string {
    return this.getMapboxFeature().source;
  }

  /**
   * Sometimes a point can be linked to a bigger polygon (e.g. an area).
   * this function will return an geometry id reference to the other geometry.
   * @returns {number | null} either the geometry identiefer or null if there is no linkable polygon.
   */
  public getPolygonLinkID(): number | null {
    return this.getProperty('polygon_link_id') as number | null;
  }

  /**
   * The idea for a sextant is that a user can have an idea in which "quadrant" the geomtry is.
   * Examples are 'SE', 'NW', 'N', 'S', 'NE', 'SW', etc. etc.
   * Its worth noting this implementation is not set in stone, and might be changed.

   * @returns {string | null} the sextant direction for this feature (if available)
   */
  public getSextant(): string | null {
    return this.getProperty('sextant') as string | null;
  }

  public getSubCategoryId(): number | null {
    return this.getProperty('sub_category_id') as number | null;
  }

  public getPrimaryCategoryId(): number | null {
    return this.getProperty('primary_category_id') as number | null;
  }

  public getPrimaryCategory(): string | null {
    return this.getProperty('primary_category') as string | null;
  }

  public getSubCategory(): string | null {
    return this.getProperty('sub_category') as string | null;
  }

  public getLmId(): string | null {
    return this.getProperty('lm_id') as string | null;
  }

  public isLandmark(): boolean {
    return this.getProperty('landmark') as boolean;
  }

  public isClosed(): boolean {
    return this.getProperty('closed') as boolean;
  }

  public isAreaHighlight(): boolean {
    return this.getProperty('area_highlight') as boolean;
  }

  /**
   * A feature might need to prevent the propegation of a click event, this function will return if it should.
   * @returns {boolean}
   */
  public shouldPropagateClick(): boolean {
    return Boolean(this.getProperty('preventClickPropagation'));
  }

  public getPinType(): string {
    const replacePinType =
      this.getSource() === MMS_PIN_SOURCE_ID ? PIN_TYPE_MMS : PIN_TYPE_FEATURE;
    return (this.getProperty('pin_type') as string) || replacePinType;
  }

  /**
   * @returns {boolean} whether this feature is selectable and should receive a pin of sorts.
   */
  public isSelectable(): boolean {
    if (
      this.getGeomTypeProperty() === BaseGeometryType.DISPLAY_POINT &&
      !this.isAreaHighlight()
    ) {
      return true;
    }
    return this.getGeomTypeProperty() === BaseGeometryType.POINT;
  }

  /**
   * @returns {boolean} whether this feature is routable or not.
   */
  public isRouteable(): boolean {
    return this.getGeomTypeProperty() !== BaseGeometryType.LINESTRING;
  }

  // TODO: requires unit testing
  /**
   * Not ever geometry is elligible for clustering. This has to be set up correctly with the stylesheet & the data source.
   * @returns {boolean} whether this feature is eligible for cluster or not.
   */
  public isClusterable(): boolean {
    return Boolean(this.getProperty('cluster'));
  }

  /**
   * The only way to retrieve feature (meta) data is via our actual mapping sources.
   * that means the way to fetch this data is a little bit elaborates.
   * to be able to do it this funciton encodes a couple of parameters to allow us to retrieve the correct data.
   * @returns {string} returns a short string representation of the feature in question.
   */
  public getQueryString(): string | null {
    const centroid = this.getCentroid();
    if (!centroid) return null;

    const location = centroid.map(coord => coord.toPrecision(6)).toString();

    const id = this.getId();
    const source = this.getSource();

    return Buffer.from(`${location}@${source}@${id}`).toString('base64');
  }

  /**
   * Returns the internal map feature data. Be carefull about using this.
   * @returns {MapboxGeoJSONFeature}
   */
  // TODO: method should be private, usuage of this function externally to be replaced by a static LMFeature.Builder.
  public getMapboxFeature(): MapboxGeoJSONFeature {
    return this.mapboxFeature;
  }

  private getGeometry(): GeoJSON.Geometry {
    return this.mapboxFeature.geometry;
  }

  private getProperty(name: string): string | null | boolean | number {
    const properties = this.mapboxFeature.properties || {};
    if (properties[name]) {
      return properties[name] === 'null' ? null : properties[name];
    }

    return null;
  }
}
